import { z } from "zod";
import { emailSchema } from "../common/index.js";

const reverseDomainsRegex = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
const googleClientIdSuffix = ".apps.googleusercontent.com";
const googleReversedClientIdPrefix = "com.googleusercontent.apps.";

const httpsUrl = z.url().refine((v) => v.startsWith("https://"), { message: "URL must use HTTPS" });

export const updateCommunityMobileAppSchema = z
	.object({
		enabled: z.boolean(),
		privacy_content: z.string().nullable(),
		terms_of_use_content: z.string().nullable(),
		contact_email: emailSchema.nullable(),
		app_name: z.string().trim().min(1).max(60).nullable(),
		slug: z
			.string()
			.min(2)
			.max(50)
			.regex(/^[a-z0-9-]+$/, { message: "Slug must be lowercase alphanumeric with hyphens" })
			.nullable(),
		color_primary: z.string().regex(hexColorRegex, { message: "Must be a valid hex color (#RRGGBB)" }).nullable(),
		color_secondary: z.string().regex(hexColorRegex, { message: "Must be a valid hex color (#RRGGBB)" }).nullable(),
		color_splash: z.string().regex(hexColorRegex, { message: "Must be a valid hex color (#RRGGBB)" }).nullable(),
		icon_url: httpsUrl.nullable(),
		splash_url: httpsUrl.nullable(),
		adaptive_icon_url: httpsUrl.nullable(),
		bundle_id: z
			.string()
			.min(3)
			.max(155)
			.regex(reverseDomainsRegex, { message: "Must use reverse-domain notation (e.g. com.example.app)" })
			.nullable(),
		package_name: z
			.string()
			.min(3)
			.max(155)
			.regex(reverseDomainsRegex, { message: "Must use reverse-domain notation (e.g. com.example.app)" })
			.nullable(),
		scheme: z
			.string()
			.min(2)
			.max(40)
			.regex(/^[a-z][a-z0-9]*$/, { message: "Scheme must be lowercase alphanumeric starting with a letter" })
			.nullable(),
		eas_project_id: z.uuid().nullable(),
		asc_app_id: z.string().min(6).max(15).regex(/^\d+$/, { message: "ASC App ID must contain digits only" }).nullable(),
		google_ios_client_id: z
			.string()
			.refine((v) => v.endsWith(googleClientIdSuffix), { message: `Must end with ${googleClientIdSuffix}` })
			.nullable(),
		google_ios_reversed_client_id: z
			.string()
			.refine((v) => v.startsWith(googleReversedClientIdPrefix), {
				message: `Must start with ${googleReversedClientIdPrefix}`,
			})
			.nullable(),
		google_android_client_id: z
			.string()
			.refine((v) => v.endsWith(googleClientIdSuffix), { message: `Must end with ${googleClientIdSuffix}` })
			.nullable(),
		google_web_client_id: z
			.string()
			.refine((v) => v.endsWith(googleClientIdSuffix), { message: `Must end with ${googleClientIdSuffix}` })
			.nullable(),
	})
	.strict();

export type UpdateCommunityMobileAppPayload = z.infer<typeof updateCommunityMobileAppSchema>;
